import {
    Modal, ModalDialog, ModalOverflow, ModalClose,
    Box, Sheet, Stack, Typography, IconButton, Button,
    Divider, Chip, Input, Tooltip, CircularProgress, Select, Option,
} from "@mui/joy";
import { useState, useEffect, useCallback, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutContext } from "../../context";

import CloseRoundedIcon            from "@mui/icons-material/CloseRounded";
import LinkRoundedIcon             from "@mui/icons-material/LinkRounded";
import ContentCopyRoundedIcon      from "@mui/icons-material/ContentCopyRounded";
import DeleteOutlineRoundedIcon    from "@mui/icons-material/DeleteOutlineRounded";
import AddRoundedIcon              from "@mui/icons-material/AddRounded";
import CheckRoundedIcon            from "@mui/icons-material/CheckRounded";
import VisibilityRoundedIcon       from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon             from "@mui/icons-material/EditRounded";
import CommentRoundedIcon          from "@mui/icons-material/CommentRounded";
import CalendarMonthRoundedIcon    from "@mui/icons-material/CalendarMonthRounded";
import RepeatRoundedIcon           from "@mui/icons-material/RepeatRounded";
import FolderRoundedIcon           from "@mui/icons-material/FolderRounded";
import AssignmentRoundedIcon       from "@mui/icons-material/AssignmentRounded";
import ToggleOffRoundedIcon        from "@mui/icons-material/ToggleOffRounded";
import ToggleOnRoundedIcon         from "@mui/icons-material/ToggleOnRounded";

import {
    APICreateShareLink,
    APIGetShareLinks,
    APIUpdateShareLink,
    APIDeleteShareLink,
} from "../../api";

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCESS_LEVELS = [
    { value: "view",    label: "Просмотр",       icon: <VisibilityRoundedIcon sx={{ fontSize: 14 }} />, color: "primary" },
    { value: "comment", label: "Комментарии",    icon: <CommentRoundedIcon    sx={{ fontSize: 14 }} />, color: "primary" },
    { value: "edit",    label: "Редактирование", icon: <EditRoundedIcon       sx={{ fontSize: 14 }} />, color: "primary" },
];

const EXPIRES_OPTIONS = [
    { value: null, label: "Бессрочно" },
    { value: 1,    label: "1 день" },
    { value: 7,    label: "7 дней" },
    { value: 30,   label: "30 дней" },
    { value: 90,   label: "90 дней" },
];

const MAX_USES_OPTIONS = [
    { value: null, label: "Безлимит" },
    { value: 1,    label: "1 раз" },
    { value: 5,    label: "5 раз" },
    { value: 10,   label: "10 раз" },
    { value: 50,   label: "50 раз" },
    { value: 100,  label: "100 раз" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildShareUrl(token) {
    return `${window.location.origin}/share/${token}`;
}

function formatExpiry(isoStr) {
    if (!isoStr) return "Бессрочно";
    const d = new Date(isoStr);
    const now = new Date();
    const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
    if (diff < 0) return "Истекла";
    if (diff === 0) return "Сегодня";
    if (diff === 1) return "Завтра";
    return `${diff} дн.`;
}

// ─── LinkRow ──────────────────────────────────────────────────────────────────

function LinkRow({ link, onToggle, onDelete, copied, onCopy }) {
    const level = ACCESS_LEVELS.find(l => l.value === link.access_level) || ACCESS_LEVELS[0];
    const expired = link.expires_at && new Date(link.expires_at) < new Date();

    return (
        <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: -20, height: 0 }}
            transition={{ duration: 0.18 }}
        >
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 12,
                bgcolor: link.is_active && !expired ? "neutral.50" : "neutral.100",
                border: "1.5px solid",
                borderColor: link.is_active && !expired ? "neutral.200" : "neutral.300",
                opacity: link.is_active && !expired ? 1 : 0.55,
                transition: "all 0.15s",
                mb: 0.75,
            }}>
                {/* Иконка уровня */}
                <Box sx={{
                    width: 32, height: 32, borderRadius: 8,
                    bgcolor: `${level.color}.softBg`,
                    color: `${level.color}.plainColor`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                }}>
                    {level.icon}
                </Box>

                {/* Инфо */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.2 }}>
                        <Chip size="sm" variant="soft" color={level.color}
                            sx={{ fontSize: "0.68rem", height: 18, px: 0.75 }}>
                            {level.label}
                        </Chip>
                        {expired && (
                            <Chip size="sm" variant="soft" color="danger"
                                sx={{ fontSize: "0.68rem", height: 18, px: 0.75 }}>
                                Истекла
                            </Chip>
                        )}
                        {!link.is_active && !expired && (
                            <Chip size="sm" variant="soft" color="neutral"
                                sx={{ fontSize: "0.68rem", height: 18, px: 0.75 }}>
                                Откл.
                            </Chip>
                        )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.tertiary" }}>
                            {link.uses_count}{link.max_uses ? `/${link.max_uses}` : ""} исп.
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.tertiary" }}>·</Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "text.tertiary" }}>
                            {formatExpiry(link.expires_at)}
                        </Typography>
                    </Stack>
                </Box>

                {/* Действия */}
                <Stack direction="row" spacing={0.25} alignItems="center">
                    <Tooltip title="Копировать ссылку" size="sm">
                        <IconButton size="sm" variant="plain" color="neutral"
                            sx={{ width: 28, height: 28, minWidth: 0, borderRadius: 8 }}
                            onClick={() => onCopy(link.token)}
                        >
                            {copied === link.token
                                ? <CheckRoundedIcon sx={{ fontSize: 18, color: "success.500" }} />
                                : <ContentCopyRoundedIcon sx={{ fontSize: 18 }} />
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={link.is_active ? "Деактивировать" : "Активировать"} size="sm">
                        <IconButton size="sm" variant="plain"
                            color={link.is_active ? "primary" : "neutral"}
                            sx={{ width: 28, height: 28, minWidth: 0, borderRadius: 8 }}
                            onClick={() => onToggle(link)}
                        >
                            {link.is_active
                                ? <ToggleOnRoundedIcon  sx={{ fontSize: 22 }} />
                                : <ToggleOffRoundedIcon sx={{ fontSize: 22 }} />
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Удалить" size="sm">
                        <IconButton size="sm" variant="plain" color="danger"
                            sx={{ width: 28, height: 28, minWidth: 0, borderRadius: 8 }}
                            onClick={() => onDelete(link.token)}
                        >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
        </motion.div>
    );
}

// ─── CreateForm ───────────────────────────────────────────────────────────────

function CreateForm({ onSubmit, loading }) {
    const [accessLevel, setAccessLevel] = useState("view");
    const [expiresIn,   setExpiresIn]   = useState(null);
    const [maxUses,     setMaxUses]     = useState(null);

    return (
        <Box sx={{
            p: 1.5, borderRadius: 14,
            border: "1.5px dashed", borderColor: "primary.200",
            bgcolor: "primary.50",
        }}>
            <Typography level="body-xs" sx={{
                fontWeight: 700, color: "primary.plainColor",
                textTransform: "uppercase", letterSpacing: "0.07em",
                fontSize: "0.8rem", mb: 1.5,
            }}>
                Новая ссылка
            </Typography>

            <Stack spacing={1}>
                {/* Уровень доступа */}
                <Box>
                    <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "0.7rem", mb: 0.5, fontWeight: 600 }}>
                        Уровень доступа
                    </Typography>
                    <Stack direction="row" flexWrap={'wrap'} sx={{ gap: 0.5 }}>
                        {ACCESS_LEVELS.map(l => (
                            <Box key={l.value}
                                onClick={() => setAccessLevel(l.value)}
                                sx={{
                                    flex: 1, display: "flex", alignItems: "center",
                                    justifyContent: "center", gap: 0.5,
                                    px: 1, py: 0.75, borderRadius: 10, cursor: "pointer",
                                    border: "1.5px solid",
                                    borderColor: accessLevel === l.value ? `${l.color}.300` : "neutral.200",
                                    bgcolor: accessLevel === l.value ? `${l.color}.softBg` : "background.surface",
                                    color: accessLevel === l.value ? `${l.color}.plainColor` : "text.secondary",
                                    transition: "all 0.12s",
                                }}
                            >
                                {l.icon}
                                <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "inherit" }}>
                                    {l.label}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>

                <Stack direction="row" spacing={1}>
                    {/* Срок действия */}
                    <Box sx={{ flex: 1 }}>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "0.7rem", mb: 0.5, fontWeight: 600 }}>
                            Срок
                        </Typography>
                        <Select size="sm" value={expiresIn} onChange={(_, v) => setExpiresIn(v)}
                            sx={{ borderRadius: 10, fontSize: "0.8rem" }}>
                            {EXPIRES_OPTIONS.map(o => (
                                <Option key={String(o.value)} value={o.value}>{o.label}</Option>
                            ))}
                        </Select>
                    </Box>

                    {/* Макс. использований */}
                    <Box sx={{ flex: 1 }}>
                        <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "0.7rem", mb: 0.5, fontWeight: 600 }}>
                            Исп.
                        </Typography>
                        <Select size="sm" value={maxUses} onChange={(_, v) => setMaxUses(v)}
                            sx={{ borderRadius: 10, fontSize: "0.8rem" }}>
                            {MAX_USES_OPTIONS.map(o => (
                                <Option key={String(o.value)} value={o.value}>{o.label}</Option>
                            ))}
                        </Select>
                    </Box>
                </Stack>

                <Button size="sm" loading={loading}
                    startDecorator={<AddRoundedIcon sx={{ fontSize: 15 }} />}
                    onClick={() => onSubmit({ accessLevel, expiresIn, maxUses })}
                    sx={{ borderRadius: 10, alignSelf: "flex-end", px: 2 }}
                >
                    Создать ссылку
                </Button>
            </Stack>
        </Box>
    );
}

// ─── ShareModal ───────────────────────────────────────────────────────────────

export default function ShareModal() {
    const { shareModal, setShareModal } = useContext(LayoutContext);

    const open        = shareModal?.open ?? false;
    const targetType  = shareModal?.targetType;   // "folder" | "tasklist"
    const targetId    = shareModal?.targetId;
    const targetName  = shareModal?.targetName ?? "Объект";

    const [links,       setLinks]       = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [creating,    setCreating]    = useState(false);
    const [showCreate,  setShowCreate]  = useState(false);
    const [copied,      setCopied]      = useState(null);

    const close = () => setShareModal({ open: false });

    // ── Загрузка ссылок ───────────────────────────────────────────────────────
    const loadLinks = useCallback(async () => {
        if (!targetId || !targetType) return;
        setLoadingList(true);
        try {
            const data = await APIGetShareLinks(targetType, targetId);
            setLinks(data);
        } catch {}
        finally { setLoadingList(false); }
    }, [targetId, targetType]);

    useEffect(() => {
        if (open) { setLinks([]); setShowCreate(false); loadLinks(); }
    }, [open, loadLinks]);

    // ── Создать ───────────────────────────────────────────────────────────────
    const handleCreate = async ({ accessLevel, expiresIn, maxUses }) => {
        setCreating(true);
        try {
            const sl = await APICreateShareLink({
                target_type:     targetType,
                target_id:       targetId,
                access_level:    accessLevel,
                expires_in_days: expiresIn,
                max_uses:        maxUses,
            });
            setLinks(p => [sl, ...p]);
            setShowCreate(false);
        } catch {}
        finally { setCreating(false); }
    };

    // ── Копировать ────────────────────────────────────────────────────────────
    const handleCopy = (token) => {
        navigator.clipboard.writeText(buildShareUrl(token)).catch(() => {});
        setCopied(token);
        setTimeout(() => setCopied(null), 2000);
    };

    // ── Переключить активность ────────────────────────────────────────────────
    const handleToggle = async (link) => {
        const updated = { ...link, is_active: !link.is_active };
        setLinks(p => p.map(l => l.token === link.token ? updated : l));
        try { await APIUpdateShareLink(link.token, { is_active: !link.is_active }); }
        catch { setLinks(p => p.map(l => l.token === link.token ? link : l)); }
    };

    // ── Удалить ───────────────────────────────────────────────────────────────
    const handleDelete = async (token) => {
        if (!window.confirm("Удалить ссылку? Все кто её использовал потеряют доступ.")) return;
        setLinks(p => p.filter(l => l.token !== token));
        try { await APIDeleteShareLink(token); }
        catch { loadLinks(); }
    };

    const Icon = targetType === "folder" ? FolderRoundedIcon : AssignmentRoundedIcon;

    return (
        <AnimatePresence>
            {open && (
                <Modal open onClose={close}>
                    <ModalOverflow>
                        <ModalDialog
                            sx={{
                                p: 0, borderRadius: 20, overflow: "hidden",
                                maxWidth: 460, width: "100%", gap: 0,
                                boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
                            }}
                        >
                            {/* Шапка */}
                            <Box sx={{ backgroundColor: "neutral.50", boxShadow: 'lg', borderRadius: 15, px: 1, pt: 1, pb: 1, m: 1,
                                display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                                    <Box sx={{
                                        width: 40, height: 40, borderRadius: 12,
                                        bgcolor: "primary.200",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        flexShrink: 0,
                                    }}>
                                        <Icon sx={{ fontSize: 20 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{
                                            fontWeight: 700, fontSize: "1rem",
                                            lineHeight: 1.2,
                                        }} noWrap>
                                            Поделиться
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.78rem", lineHeight: 1 }} noWrap>
                                            {targetName}
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={close} size="sm" variant="plain">
                                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Stack>
                            </Box>

                            {/* Контент */}
                            <Box sx={{ px: 2, pt: 2, pb: 2.5, maxHeight: "70vh", overflowY: "auto" }}>

                                {/* Заголовок секции */}
                                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <LinkRoundedIcon sx={{ fontSize: 15, color: "text.tertiary" }} />
                                        <Typography level="body-xs" sx={{
                                            fontWeight: 700, color: "text.secondary",
                                            textTransform: "uppercase", letterSpacing: "0.07em", fontSize: "0.65rem",
                                        }}>
                                            Ссылки доступа
                                        </Typography>
                                        {links.length > 0 && (
                                            <Chip size="sm" variant="soft" color="neutral"
                                                sx={{ fontSize: "0.65rem", height: 18, px: 0.75 }}>
                                                {links.length}
                                            </Chip>
                                        )}
                                    </Stack>
                                    <Button size="sm" variant="soft" color="primary"
                                        startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => setShowCreate(v => !v)}
                                        sx={{ borderRadius: 10, height: 28, px: 1.25, fontSize: "0.78rem" }}
                                    >
                                        Создать
                                    </Button>
                                </Stack>

                                {/* Форма создания */}
                                <AnimatePresence>
                                    {showCreate && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            style={{ overflow: "hidden", marginBottom: 12 }}
                                        >
                                            <CreateForm onSubmit={handleCreate} loading={creating} />
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Список ссылок */}
                                {loadingList ? (
                                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                ) : links.length === 0 ? (
                                    <Box sx={{
                                        py: 4, textAlign: "center",
                                        color: "text.tertiary",
                                    }}>
                                        <LinkRoundedIcon sx={{ fontSize: 36, opacity: 0.2, mb: 1 }} />
                                        <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                                            Ссылок пока нет
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.5 }}>
                                            Создайте ссылку чтобы поделиться
                                        </Typography>
                                    </Box>
                                ) : (
                                    <AnimatePresence>
                                        {links.map(link => (
                                            <LinkRow
                                                key={link.token}
                                                link={link}
                                                copied={copied}
                                                onCopy={handleCopy}
                                                onToggle={handleToggle}
                                                onDelete={handleDelete}
                                            />
                                        ))}
                                    </AnimatePresence>
                                )}

                                {/* Подсказка */}
                                <Box sx={{
                                    mt: 2, px: 1.5, py: 1.25, borderRadius: 10,
                                    bgcolor: "neutral.100",
                                    display: "flex", alignItems: "flex-start", gap: 1,
                                }}>
                                    <LinkRoundedIcon sx={{ fontSize: 13, color: "text.tertiary", mt: 0.2, flexShrink: 0 }} />
                                    <Typography level="body-xs" sx={{ color: "text.tertiary", lineHeight: 1.5 }}>
                                        Ссылки позволяют открыть доступ без регистрации. Гость увидит только тот объект, на который выдана ссылка.
                                    </Typography>
                                </Box>
                            </Box>
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}